import { fill, isEmpty, take } from 'lodash';
import {
  type GoodsItem,
  isGoodsEmpty,
  transformGoodsToInput,
} from '../../../common/goods/goodsUtils';
import type { VehicleType } from '../../../common/graphql/fragments/gql/VehicleType';
import { animalDropdownNoOption } from '../../../common/graphql/useAnimalTypesOptions';
import { customsClassificationDropdownEmptyOption } from '../../../common/graphql/useCustomsClassificationsOptions';
import { loadingPreferenceDropdownNoOption } from '../../../common/graphql/useLoadingPreferenceOptions';
import { isVisible } from '../../../common/utils/bookingFields';
import { formatRegistrationNumberString } from '../../../common/utils/stringUtil';
import type { CreateBookingInput } from '../../../gql/graphql';
import { createCreateCandidateMock } from '../../../services/create/_mocks/createCandidateMock';
import type { CreateCandidate, CreateFormState } from '../../../services/create/createFormReducer';
import { getNumberOfDrivers } from '../../../services/create/utils/transformCreateBookingResults';
import { getCreateFormFieldsToShow, SpecificFieldId } from './createFormFieldsUtil';

export const getInitialCreateCandidate = (
  vehicleType: VehicleType | null,
  height: number | undefined,
  length: number | undefined,
  width: number | undefined,
  tradeWeight: number | undefined,
  goodsItem?: GoodsItem | undefined,
): CreateCandidate => {
  return {
    empty: false,
    customerRef: '',
    customsClassification: customsClassificationDropdownEmptyOption.data,
    hasAnimals: false,
    hasElectricalPlugin: false,
    hasHazardousGoods: false,
    height,
    length,
    livestockType: animalDropdownNoOption.data,
    loadingListMessage: '',
    loadingPreference: loadingPreferenceDropdownNoOption.data,
    meta: {
      errors: [],
      warnings: [],
    },
    importReference: '',
    exportReference: '',
    numberOfDrivers: getNumberOfDrivers(vehicleType),
    pluginTemperature: '',
    receiverCountry: undefined,
    registrationNumberAlert: null,
    senderCountry: undefined,
    tradeWeight,
    trailerReg: '',
    vehicleReg: '',
    vehicleType,
    width,
    goods: goodsItem ? { [goodsItem.id]: goodsItem } : {},
  };
};

export const ensureAllBookingCandidatesHasValues = (
  bookings: CreateCandidate[],
  numberOfCandidates: number,
): CreateCandidate[] => {
  const firstNoConflictingBooking = findCandidateWithoutRegNumberAlert(bookings) || bookings[0];

  const takenCandidate = take(bookings, numberOfCandidates);

  const candidates = takenCandidate.map(candidate =>
    candidate
      ? candidate
      : getInitialCreateCandidate(
          firstNoConflictingBooking.vehicleType,
          firstNoConflictingBooking.height,
          firstNoConflictingBooking.length,
          firstNoConflictingBooking.width,
          firstNoConflictingBooking.tradeWeight,
        ),
  );

  const defaultCandidates = fill(
    Array(numberOfCandidates - candidates.length),
    getInitialCreateCandidate(
      firstNoConflictingBooking.vehicleType,
      firstNoConflictingBooking.height,
      firstNoConflictingBooking.length,
      firstNoConflictingBooking.width,
      firstNoConflictingBooking.tradeWeight,
    ),
  );

  return candidates.concat(defaultCandidates);
};

export const transformBookingCandidates = (formState: CreateFormState): CreateBookingInput[] => {
  const transform = transformCandidate(formState);
  return formState.bookings.map(b => transform(b));
};

export const transformCandidate =
  (formState: CreateFormState) =>
  (candidate: CreateCandidate): CreateBookingInput => {
    const fieldsToShow = getCreateFormFieldsToShow(formState);

    if (!formState.sailing) {
      throw new Error('No sailing selected');
    }

    if (!candidate.vehicleType) {
      throw new Error('No vehicle type selected');
    }

    return {
      importReference: candidate.importReference || undefined,
      exportReference: candidate.exportReference || undefined,
      custNo: formState.customer?.custNo || 0,
      customerReference: isVisible(fieldsToShow, SpecificFieldId.CustomerReference)
        ? candidate.customerRef || ''
        : '',
      customsClassificationCode:
        isVisible(fieldsToShow, SpecificFieldId.CustomsClassification) &&
        candidate.customsClassification &&
        candidate.customsClassification !== customsClassificationDropdownEmptyOption.data
          ? candidate.customsClassification.value
          : '',
      hazardousGoods:
        isVisible(fieldsToShow, SpecificFieldId.HazardousGoods) &&
        !candidate.empty &&
        candidate.hasHazardousGoods,
      height: isVisible(fieldsToShow, SpecificFieldId.Height) ? candidate.height || 0 : 0,
      length: isVisible(fieldsToShow, SpecificFieldId.Length) ? candidate.length || 0 : 0,
      livestockTypeCode:
        isVisible(fieldsToShow, SpecificFieldId.LivestockTypeCode) &&
        candidate.hasAnimals &&
        !candidate.empty &&
        candidate.livestockType !== animalDropdownNoOption.data
          ? candidate.livestockType.value
          : '',
      loadingListMessage: isVisible(fieldsToShow, SpecificFieldId.LoadingListMessage)
        ? candidate.loadingListMessage || ''
        : '',
      loadingPreferenceCode:
        isVisible(fieldsToShow, SpecificFieldId.LoadingPreferenceCode) &&
        candidate.loadingPreference !== loadingPreferenceDropdownNoOption.data
          ? candidate.loadingPreference.value
          : '',
      noOfAdults: 0,
      noOfChildren: 0,
      noOfDrivers: isVisible(fieldsToShow, SpecificFieldId.NoOfDrivers)
        ? candidate.numberOfDrivers || 0
        : 0,
      noOfInfants: 0,
      noOfPlugins:
        isVisible(fieldsToShow, SpecificFieldId.Temperature) && candidate.hasElectricalPlugin
          ? 1
          : 0,
      pluginTemperature:
        isVisible(fieldsToShow, SpecificFieldId.Temperature) && candidate.hasElectricalPlugin
          ? candidate.pluginTemperature || ''
          : '',
      sailingId: formState.sailing.id || '',
      tradeWeight: isVisible(fieldsToShow, SpecificFieldId.TradeWeight)
        ? candidate.tradeWeight || 0
        : 0,
      trailerRegNo: formatRegistrationNumberString(candidate.trailerReg ?? ''),
      vehicleRegNo: formatRegistrationNumberString(candidate.vehicleReg ?? ''),
      vehicleTypeCode:
        isVisible(fieldsToShow, SpecificFieldId.VehicleTypeCode) && candidate.vehicleType
          ? candidate.vehicleType.code
          : '',
      width: isVisible(fieldsToShow, SpecificFieldId.Width) ? candidate.width || 0 : 0,
      senderCountryCode:
        isVisible(fieldsToShow, SpecificFieldId.SenderCountryCode) &&
        !isEmpty(candidate.goods) &&
        !candidate.empty &&
        candidate.senderCountry
          ? candidate.senderCountry.value
          : '',
      receiverCountryCode:
        isVisible(fieldsToShow, SpecificFieldId.ReceiverCountryCode) &&
        !isEmpty(candidate.goods) &&
        !candidate.empty &&
        candidate.receiverCountry
          ? candidate.receiverCountry.value
          : '',
      goods:
        !candidate.empty && !isGoodsEmpty(candidate.goods)
          ? transformGoodsToInput(candidate.goods)
          : [],
    };
  };

export const findCandidateWithoutRegNumberAlert = (
  bookings: CreateCandidate[],
): CreateCandidate | undefined => {
  return bookings.find(booking => booking.registrationNumberAlert === null);
};

export const getCreateCandidate = (candidates: CreateCandidate[], index: number): CreateCandidate =>
  candidates[index] || createCreateCandidateMock();
